import { FacebookIcon, InstagramIcon, SnapchatIcon, TikTokIcon, TripadvisorIcon, XIcon, YelpIcon } from "@xola/icons";
import { flash, FormGroup, Label, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, nullableString, requiredString } from "common";
import React, { useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { Geo, Permission, Seller } from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { xolaUri } from "../../api/xolaUri";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { getDefaultLogoUrl } from "../../models/Seller";
import { CitySelect, getCityString, getCountryString, getStateString } from "../Common/CitySelect";
import { Form } from "../Common/Form";
import { ImageUploadInput } from "../Common/ImageUploadInput";
import { IsGranted } from "../Common/IsGranted";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { PrefixField } from "../Field/PrefixField";
import { ContentLayout } from "../Layout/ContentLayout";

const initialValues = {
    name: "",
    phone: "",
    website: "",
    streetAddress: "",
    zip: "",
    city: "",
    meta: {
        facebook: "",
        twitter: "",
        tripAdvisor: "",
        yelp: "",
        instagram: "",
        snapchat: "",
        tiktok: "",
    },
};

const socialLinks = [
    { name: "facebook", label: "Facebook", icon: FacebookIcon },
    { name: "twitter", label: "X", icon: XIcon },
    { name: "tripAdvisor", label: "TripAdvisor", icon: TripadvisorIcon },
    { name: "yelp", label: "Yelp", icon: YelpIcon },
    { name: "instagram", label: "Instagram", icon: InstagramIcon },
    { name: "tiktok", label: "Tiktok", icon: TikTokIcon },
    { name: "snapchat", label: "Snapchat", icon: SnapchatIcon },
];

interface MetaLinks {
    facebook: string | null;
    snapchat: string | null;
    twitter: string | null;
    yelp: string | null;
    tripAdvisor: string | null;
    instagram: string | null;
    tiktok: string | null;
}

export const CompanyProfileForm = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const { hash } = useLocation();

    const { mutateAsync: updateProfile, isLoading } = useMutation(async (seller: Seller) => {
        return api.put(`sellers/${seller.id}`, seller);
    });

    const { mutateAsync: updateProfileMeta, isLoading: isLoadingMeta } = useMutation(async (metaValues: MetaLinks) => {
        return api.post(`sellers/${seller.id}/meta`, metaValues);
    });

    const handlePictureUpdated = () => {
        invalidate("sellers", seller.id);
    };

    const { isGranted } = useAuthorization();
    const hasAccess = isGranted(Permission.COMPANY_BASIC_INFO_VIEW);

    const handleSubmit = async (values: any) => {
        if (!hasAccess) {
            await updateProfileMeta({
                facebook: values.meta.facebook || null,
                snapchat: values.meta.snapchat || null,
                yelp: values.meta.yelp || null,
                twitter: values.meta.twitter || null,
                tripAdvisor: values.meta.tripAdvisor || null,
                instagram: values.meta.instagram || null,
                tiktok: values.meta.tiktok || null,
            });
            invalidate("sellers", seller.id);
            queryClient.invalidateQueries("account");
            flash.success("Your settings are saved");
            return;
        }

        const geo = values.meta?.geo as Geo;
        await updateProfile({
            ...values,
            city: getCityString(geo),
            countryCode: geo?.countryCode ?? values.countryCode,
            // API falls back to this offset to derive timezoneName when geo has no timeZoneId
            timezone: geo?.timezone?.gmtOffset ?? values.timezone,
        });
        invalidate("sellers", seller.id);
        queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
        flash.success("Your settings are saved");
    };

    const updatePictureUrl = `${xolaUri}/api/sellers/${seller.id}/media`;

    useEffect(() => {
        if (hash) {
            scrollToElement(hash);
        }
    }, [hash]);

    return (
        <ContentLayout isWide isDisabled={isLoading || isLoadingMeta}>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={() => getValidationSchema(hasAccess)}
                initialValues={{ ...initialValues, ...seller }}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, handleReset, values, setValues, errors }: any) => {
                    const geo = values.meta?.geo as Geo;
                    const city = geo?.geonameId ? getCityString(geo) : null;
                    const cityError = errors.meta?.geo;

                    const handleCityChange = (_: string | null, geo: Geo | null) => {
                        setValues({
                            ...values,
                            meta: {
                                ...values.meta,
                                geo: geo ?? undefined,
                            },
                            state: geo ? getStateString(geo) : null,
                            country: geo ? getCountryString(geo) : null,
                        });
                    };

                    return (
                        <Form>
                            <h2 className="mt-4">Company Profile</h2>
                            <IsGranted permissions={Permission.COMPANY_BASIC_INFO_VIEW}>
                                <ContentLayout.Section
                                    title="Basic Information"
                                    description="General information about your company. This information is displayed in email communications with your customers for reference"
                                >
                                    <div className="space-y-4">
                                        <h4 className="font-bold">Basic Information</h4>

                                        <ImageUploadInput
                                            src={getDefaultLogoUrl(seller, "medium")}
                                            uploadUrl={updatePictureUrl}
                                            hasDelete={false}
                                            onUpdated={handlePictureUpdated}
                                            onError={(error) => flash.show({ text: error, color: "danger" })}
                                        />

                                        <FormField name="name" label="Company Name">
                                            <InputField isRequired />
                                        </FormField>

                                        <FormField name="phone" label="Phone">
                                            <InputField isRequired />
                                        </FormField>

                                        <FormField name="website" label="Website">
                                            <InputField />
                                        </FormField>

                                        <FormField
                                            name="streetAddress"
                                            label="Street Address"
                                            tooltip="This appears in booking confirmation emails sent to your customers"
                                        >
                                            <InputField />
                                        </FormField>

                                        <FormField name="zip" label="Zip Code">
                                            <InputField isRequired />
                                        </FormField>

                                        <FormGroup>
                                            <Label
                                                className={clsx(
                                                    "whitespace-nowrap",
                                                    errors.meta?.geo && "!text-danger",
                                                )}
                                            >
                                                City {cityError}
                                            </Label>
                                            <CitySelect
                                                value={city}
                                                error={cityError !== undefined}
                                                onChange={handleCityChange}
                                            />
                                        </FormGroup>

                                        <FormField name="state" label="State">
                                            <InputField isRequired disabled />
                                        </FormField>

                                        <FormField name="country" label="Country">
                                            <InputField isRequired disabled />
                                        </FormField>
                                    </div>
                                </ContentLayout.Section>
                            </IsGranted>

                            <ContentLayout.Section
                                title="Social Links"
                                description="This information is displayed in email communications with your customers for reference"
                            >
                                <div id="socialLinks" className="space-y-4">
                                    {socialLinks.map((socialLink) => (
                                        <FormField
                                            key={socialLink.name}
                                            name={`meta.${socialLink.name}`}
                                            label={socialLink.label}
                                            classNames={{
                                                label: "no-translate",
                                            }}
                                        >
                                            <PrefixField prefix={<socialLink.icon size="medium" />}>
                                                <InputField className="pl-9" />
                                            </PrefixField>
                                        </FormField>
                                    ))}
                                </div>
                            </ContentLayout.Section>

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton
                                    type="submit"
                                    isLoading={isLoading || isLoadingMeta}
                                    onClick={handleSubmit}
                                >
                                    Save
                                </SubmitButton>

                                <SecondaryButton type="reset" onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </Form>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const scrollToElement = (hash: string) => {
    const elementId = hash.split("#")[1];
    const element = document.querySelector(`#${elementId}`);
    if (element) {
        element.scrollIntoView({ behavior: "smooth" });
    }
};

const getValidationSchema = (hasAccess: boolean) => {
    return object().shape({
        ...(hasAccess && {
            name: requiredString(),
            phone: nullableString().required(),
            website: nullableString(),
            streetAddress: nullableString(),
            zip: requiredString(),
            city: requiredString(),
        }),
        meta: object().shape({
            facebook: nullableString(),
            twitter: nullableString(),
            tripAdvisor: nullableString(),
            yelp: nullableString(),
            instagram: nullableString(),
            tiktok: nullableString(),
            snapchat: nullableString(),
            ...(hasAccess && {
                geo: object().required(),
            }),
        }),
    });
};
